import * as T from 'three';
import type { WorldData } from './types';
import { atmosphere } from './stylize';

/**
 * Shafts of light through the canopy (Bible §8 Jungle Floor, §9 pollen) for two draw calls:
 * one instanced set of soft additive cards, each a cylindrical billboard turned to the camera
 * around its own sun-aligned axis, faded near the lens and with distance; and a cloud of drifting
 * pollen motes wrapped around the camera. Neither writes depth, casts shadows or hides a route.
 */
/** Toward the key light (main.ts places the sun at (-35, 80, -35) over the origin). */
export const SUN_UP = new T.Vector3(-35, 80, -35).normalize();

const shaftVertex = `
    uniform float time;
    varying vec2 vUv;
    varying float vFade;
    varying float vPhase;
    void main() {
        vec3 base = instanceMatrix[3].xyz;
        vec3 axisFull = (instanceMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz;
        float len = length(axisFull);
        vec3 axis = axisFull / len;
        float width = length(instanceMatrix[0].xyz);
        vec3 center = base + axis * (uv.y * len);
        vec3 toCam = cameraPosition - center;
        vec3 side = normalize(cross(axis, toCam));
        // Soft taper: narrow where it pierces the canopy, wider where it lands.
        vec3 world = center + side * (uv.x - .5) * width * mix(1.0, .55, uv.y);
        vUv = uv;
        vPhase = base.x * .37 + base.z * .23;
        float d = length(cameraPosition - world);
        // Never a bright wall at the lens; gone in the haze.
        vFade = smoothstep(4.0, 13.0, d) * (1.0 - smoothstep(85.0, 150.0, d));
        // Cards seen end-on along their axis collapse; fade them instead of letting them flicker.
        vFade *= smoothstep(.15, .45, length(cross(axis, normalize(toCam))));
        gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
    }`;
const shaftFragment = `
    uniform float time;
    uniform vec3 tint;
    uniform float strength;
    varying vec2 vUv;
    varying float vFade;
    varying float vPhase;
    void main() {
        float across = pow(sin(3.14159 * vUv.x), 1.6);
        float along = smoothstep(0.0, .22, vUv.y) * (1.0 - smoothstep(.55, 1.0, vUv.y));
        float streak = .72 + .28 * sin(vUv.x * 19.0 + vPhase * 3.0 + vUv.y * 2.0) * sin(vUv.x * 7.0 - vPhase);
        float breathe = .82 + .18 * sin(time * .35 + vPhase * 5.0);
        float a = across * along * streak * breathe * vFade * strength;
        gl_FragColor = vec4(tint * a, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
    }`;

export class LightShafts {
    group = new T.Group();
    private shafts: T.InstancedMesh;
    motes: T.Points;
    constructor(data: WorldData, random: () => number, quality: 'high' | 'low' = 'high') {
        // Positions: a few around every region hub plus a shaft beside the swing lines every ~24 m.
        const spots: { p: T.Vector3; len: number; width: number }[] = [];
        for (const region of data.regions) {
            const [x, y, z] = region.center;
            for (let i = 0; i < 6; i++) {
                const a = random() * Math.PI * 2, d = 7 + random() * 18;
                spots.push({ p: new T.Vector3(x + Math.cos(a) * d, Math.max(-.5, y - 12 - random() * 4), z + Math.sin(a) * d), len: 34 + random() * 14, width: 1.1 + random() * 1.6 });
            }
        }
        for (const route of data.routes || []) {
            if (route.purpose !== 'flow') continue;
            let carried = 12;
            for (let i = 1; i < route.points.length; i++) {
                const a = new T.Vector3(...route.points[i - 1]), b = new T.Vector3(...route.points[i]), seg = b.distanceTo(a);
                carried += seg;
                if (carried < 24) continue;
                carried = 0;
                const dir = b.clone().sub(a).setY(0).normalize(), side = new T.Vector3(-dir.z, 0, dir.x).multiplyScalar((random() < .5 ? -1 : 1) * (6 + random() * 8));
                spots.push({ p: b.clone().add(side).setY(Math.max(-.5, b.y - 13 - random() * 3)), len: 30 + random() * 12, width: 1 + random() * 1.4 });
            }
        }
        const plane = new T.PlaneGeometry(1, 1, 1, 6).translate(.5, .5, 0);
        const material = new T.ShaderMaterial({
            uniforms: { time: atmosphere.time, tint: { value: new T.Color('#ffe2a8') }, strength: { value: quality === 'low' ? .13 : .16 } },
            vertexShader: shaftVertex, fragmentShader: shaftFragment,
            transparent: true, depthWrite: false, blending: T.AdditiveBlending, side: T.DoubleSide, fog: false,
        });
        this.shafts = new T.InstancedMesh(plane, material, spots.length);
        const m = new T.Matrix4(), q = new T.Quaternion(), tilt = SUN_UP.clone();
        spots.forEach((s, i) => {
            // A little spread in the shafts' angle keeps them from reading as a grid of rulers.
            const axis = tilt.clone().add(new T.Vector3((random() - .5) * .08, 0, (random() - .5) * .08)).normalize();
            q.setFromUnitVectors(new T.Vector3(0, 1, 0), axis);
            m.compose(s.p, q, new T.Vector3(s.width, s.len, 1));
            this.shafts.setMatrixAt(i, m);
        });
        this.shafts.frustumCulled = false;
        this.shafts.renderOrder = 10;
        this.shafts.name = 'light-shafts';
        // Pollen motes: a box of drifting specks wrapped around the camera.
        const count = quality === 'low' ? 120 : 260, seeds = new Float32Array(count * 3);
        for (let i = 0; i < count * 3; i++) seeds[i] = random();
        const g = new T.BufferGeometry();
        g.setAttribute('position', new T.BufferAttribute(seeds, 3));
        this.motes = new T.Points(g, new T.ShaderMaterial({
            uniforms: { time: atmosphere.time, box: { value: 30 } },
            vertexShader: `
                uniform float time, box;
                varying float vAlpha;
                void main() {
                    vec3 s = position;
                    vec3 drift = vec3(sin(time * .11 + s.y * 9.0) * 1.2 + time * .25, sin(time * .23 + s.x * 7.0) * .8 + time * .06, cos(time * .09 + s.z * 8.0) * 1.2 + time * .15);
                    vec3 p = mod(s * box + drift - cameraPosition + box * .5, box) + cameraPosition - box * .5;
                    vec4 mv = viewMatrix * vec4(p, 1.0);
                    float d = -mv.z;
                    vAlpha = smoothstep(1.2, 3.0, d) * (1.0 - smoothstep(10.0, 15.0, d)) * (.55 + .45 * sin(time * 1.7 + s.x * 40.0));
                    gl_PointSize = clamp(90.0 / max(d, .1), 1.5, 5.0);
                    gl_Position = projectionMatrix * mv;
                }`,
            fragmentShader: `
                varying float vAlpha;
                void main() {
                    vec2 c = gl_PointCoord - .5;
                    float a = smoothstep(.5, .1, length(c)) * vAlpha * .42;
                    gl_FragColor = vec4(vec3(1.0, .88, .6) * a, 1.0);
                    #include <tonemapping_fragment>
                    #include <colorspace_fragment>
                }`,
            transparent: true, depthWrite: false, blending: T.AdditiveBlending, fog: false,
        }));
        this.motes.frustumCulled = false;
        this.motes.renderOrder = 11;
        this.motes.name = 'pollen';
        this.group.add(this.shafts, this.motes);
    }
    get count() { return this.shafts.count; }
}
